import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Subscription } from '@/core/notifications/model';

// The form itself, with no modal chrome. Exported separately so tests can
// exercise it directly — the same pattern channel-picker.test.tsx and
// tag-field-card.test.tsx use to test those components without the wizard
// shell around them. `QuickEditNamePopover` below is the thin Modal wrapper
// (a centered dialog rather than an anchored popover — react-bootstrap's
// Overlay/Popper positioning turned out unreliable for this trigger, and
// every other modal in this app already uses this exact bare-Modal pattern,
// see subscription-wizard-modal.tsx).
export function QuickEditNamePopoverContent({
  subscription,
  onClose,
  onSaved,
}: {
  subscription: Subscription;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(subscription.name);
  const [saving, setSaving] = useState(false);

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await notificationApi.updateSubscription(subscription.id, { name: name.trim() });
      toast.success('Alarm renamed');
      onSaved();
    } catch (e) {
      toast.error('Could not rename the alarm');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <label className="form-label" htmlFor="quick-edit-name-input">
        Name
      </label>
      <input
        id="quick-edit-name-input"
        className="form-control form-control-sm mb-2"
        autoFocus
        value={name}
        disabled={saving}
        onChange={event => setName(event.target.value)}
        onKeyDown={event => event.key === 'Enter' && handleSave()}
      />
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  );
}

// Quick edit #1 of 3 on a subscription row (see subscription-list.tsx): renames
// a subscription without opening the full wizard. PATCHes only `name`.
export default function QuickEditNamePopover({
  subscription,
  onClose,
  onSaved,
}: {
  subscription: Subscription;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Modal show onHide={onClose} centered size="sm" className="notifications-settings">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 14, fontWeight: 700 }}>Rename alarm</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <QuickEditNamePopoverContent
          subscription={subscription}
          onClose={onClose}
          onSaved={onSaved}
        />
      </Modal.Body>
    </Modal>
  );
}
